import { FormManager } from 'react-form-state-manager';
import { Globe } from 'react-feather';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Locale } from '__generated__/graphql';

import { ButtonProps } from '../../common/UI/Button';
import { DebouncedInputProps } from '../../common/UI/DebouncedInput';
import { EditorProps } from '../../common/Tiptap/types';
import { MenuBarButton } from '../../common/Tiptap/Layout';
import { countTranslations } from './helpers';
import Editor from '../../common/Tiptap';
import UI from '../../common/UI';
import useEditTranslationsModal from './useEditTranslationsModal';

export interface TranslatableInputProps extends Omit<DebouncedInputProps, 'form'> {
  form: FormManager;
  name: string;
  defaultLocale: Locale;
  title?: string;
}

export const TranslatableInput = ({
  form, name, defaultLocale, title, maxLength, ...props
}: TranslatableInputProps) => {
  const inputRef = useRef<HTMLInputElement>();
  const defaultValue = form.get(name);
  const translations = form.get(`${name}_translations`);
  const translationsCount = countTranslations(translations, defaultLocale);

  const [openModal] = useEditTranslationsModal({
    defaultLocale,
    defaultValue,
    translations,
    maxLength,
    title,
    onSave: (defaultValue, translations) => {
      form.set(name, defaultValue);
      form.set(`${name}_translations`, translations);

      inputRef.current.value = defaultValue;
    },
  });

  const [compact, setCompact] = useState(true);

  useEffect(() => {
    if (inputRef.current) {
      setCompact(inputRef.current.clientWidth < 200);
    }
  }, [inputRef]);

  return (
    <UI.InputWrapper>
      <UI.DebouncedInput
        {...form.text(name)}
        maxLength={maxLength}
        {...props}
        ref={inputRef}
      />
      <TranslateButton
        onClick={openModal}
        title={title}
        count={translationsCount}
        input={inputRef.current}
        defaultLocale={defaultLocale}
        compact={compact}
        sc={props.sc}
      />
    </UI.InputWrapper>
  );
};

export interface TranslatableEditorProps extends Partial<EditorProps> {
  form: FormManager;
  name: string;
  defaultLocale: Locale;
  brandColor?: string;
  title?: string;
}

export const TranslatableEditor = ({
  form, name, defaultLocale, title, ...editorProps
}: TranslatableEditorProps) => {
  const { t } = useTranslation();

  const defaultValue = form.get(name);
  const translations = form.get(`${name}_translations`);
  const translationsCount = countTranslations(translations, defaultLocale);

  const [key, setKey] = useState(0);

  const [openModal] = useEditTranslationsModal({
    defaultLocale,
    defaultValue,
    translations,
    title,
    inputType: 'text',
    editorProps,
    onSave: (defaultValue, translations) => {
      form.set(name, defaultValue);
      form.set(`${name}_translations`, translations);

      setKey((key) => key + 1);
    },
  });

  return (
    <Editor
      value={form.values[name]}
      onChange={(value) => form.set(name, value)}
      translateButton={(compact) => (
        <MenuBarButton
          onClick={openModal}
          aria-label={t(title ? 'translate_option' : 'translate', { option: title })}
        >
          <UI.Icon sc={{ mb: 0.4 }}>
            <Globe />
          </UI.Icon>
          {!compact && ` ${t(`common:locales.${defaultLocale}`)}`}
          {translationsCount > 0 && (compact ? ` +${translationsCount}` : ` (+${translationsCount})`)}
        </MenuBarButton>
      )}
      key={key}
      {...editorProps}
    />
  );
};

interface TranslateButtonProps {
  onClick: () => void;
  title?: string;
  count?: number;
  defaultLocale: Locale;
  compact?: boolean;
  input?: HTMLInputElement;
  sc?: ButtonProps;
}

export const TranslateButton = ({
  onClick, title, defaultLocale, count = 0, compact = false, input, sc,
}: TranslateButtonProps) => {
  const { t } = useTranslation();

  const buttonRef = useRef<HTMLButtonElement>();

  useEffect(() => {
    if (input) {
      const { paddingRight } = input.style;
      input.style.paddingRight = `${Math.max((buttonRef.current?.offsetWidth || 0) + 10, 0)}px`;

      return () => {
        input.style.paddingRight = `${paddingRight}px`;
      };
    }

    return () => {};
  });

  return (
    <UI.Button
      onClick={onClick}
      sc={{
        blank: true, color: 'gray.800', small: true, ...sc,
      }}
      aria-label={t(title ? 'translate_option' : 'translate', { option: title })}
      ref={buttonRef}
    >
      <UI.Icon sc={{ mb: 0.4 }}>
        <Globe />
      </UI.Icon>
      {!compact && ` ${t(`common:locales.${defaultLocale}`)}`}
      {count > 0 && (compact ? ` +${count}` : ` (+${count})`)}
    </UI.Button>
  );
};

export default undefined;
